'use client';

import { IMenuItem } from '@/types/nav';
import clsx from 'clsx';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname, useSearchParams } from 'next/navigation';
import { FC, memo, useId, useState } from 'react';
import { ChevronDown, ChevronRight, LockKeyhole } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface IMenuItemProps {
  menuItem: IMenuItem;
  onCloseSidebar?: () => void;
  expandedSidebar: boolean;
  locked?: boolean;
}

/**
 * Whether this item is the view the user is looking at: the pathname is inside its route (or one
 * of its `activePaths`) AND every query param its href pins matches the URL. The query half is
 * what makes a sub-nav item work when it differs from its parent only by `?tab=` — on pathname
 * alone, Campaigns and Campaigns > Templates would both read as active on both pages.
 */
const matchesCurrentView = (
  item: Pick<IMenuItem, 'href' | 'activePaths'>,
  pathName: string,
  searchParams: URLSearchParams | null,
): boolean => {
  if (item.activePaths?.some((prefix) => pathName.startsWith(prefix))) return true;
  const [itemPath, itemQuery] = item.href.split('?');
  if (!pathName.includes(itemPath)) return false;
  if (!itemQuery) return true;
  return Array.from(new URLSearchParams(itemQuery)).every(
    ([key, value]) => searchParams?.get(key) === value,
  );
};

const MenuItemComponent: FC<IMenuItemProps> = ({ menuItem, onCloseSidebar, expandedSidebar, locked = false }) => {
  const pathName = usePathname() ?? '';
  const searchParams = useSearchParams();
  const children = menuItem.children ?? [];
  const activeChild = children.find((child) => matchesCurrentView(child, pathName, searchParams));
  // A child owns the highlight on its own page; the parent then reads as the section it is,
  // not as a second current view.
  const isActive = !activeChild && matchesCurrentView(menuItem, pathName, searchParams);

  const childrenId = useId();
  const location = `${pathName}?${searchParams?.toString() ?? ''}`;
  const [collapsedAt, setCollapsedAt] = useState<string | null>(null);
  // A navigation to another view reveals the children again, including deep links.
  const childrenOpen = collapsedAt !== location;
  const handleClickNavItem = () => onCloseSidebar?.();

  if (locked) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className='mbn-nav-link mbn-locked' role='button' aria-disabled='true' aria-label={`${menuItem.label} · Locked`} tabIndex={0}>
              <menuItem.icon aria-hidden='true' color='currentColor' width={16} height={16} strokeWidth={1.5} />
              {expandedSidebar && <><span className='mbn-label'>{menuItem.label}</span><LockKeyhole className='mbn-lock' aria-hidden='true' /></>}
            </div>
          </TooltipTrigger>
          <TooltipContent side='right'>
            <p>Upgrade your plan to unlock {menuItem.label}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <>
      <div className='mbn-nav-line'>
        <Link
          className={clsx('mbn-nav-link', isActive && 'mbn-current', children.length > 0 && expandedSidebar && 'mbn-has-children')}
          href={menuItem.href}
          aria-label={menuItem.label}
          aria-current={isActive ? 'page' : undefined}
          title={!expandedSidebar ? menuItem.label : undefined}
          onClick={handleClickNavItem}
        >
          <menuItem.icon aria-hidden='true' color='currentColor' width={16} height={16} strokeWidth={1.5} />
          {expandedSidebar && <span className='mbn-label'>
            {menuItem.label}
            {menuItem.label === 'Content' && <Image src='/BETA.svg' alt='Beta' width={50} height={20} />}
          </span>}
        </Link>
        {children.length > 0 && expandedSidebar && (
          <button type='button' className='mbn-disclosure' aria-label={`${childrenOpen ? 'Collapse' : 'Expand'} ${menuItem.label}`}
            aria-expanded={childrenOpen} aria-controls={childrenId}
            onClick={() => setCollapsedAt(childrenOpen ? location : null)}>
            {childrenOpen ? <ChevronDown aria-hidden='true' /> : <ChevronRight aria-hidden='true' />}
          </button>
        )}
      </div>
      {children.length > 0 && expandedSidebar && (
        <ul id={childrenId} className='mbn-children' hidden={!childrenOpen}>
          {children.map((child) => (
            <li key={child.href}>
              <Link className={clsx('mbn-nav-link', child === activeChild && 'mbn-current')}
                href={child.href} aria-current={child === activeChild ? 'page' : undefined} onClick={handleClickNavItem}>
                <span className='mbn-label'>{child.label}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </>
  );
};

// Memoize MenuItem to prevent unnecessary re-renders when parent re-renders
// Note: We cannot check pathname in comparison because it's from usePathname() hook
// The component will still re-render when pathname changes (Next.js triggers it)
// This memo only prevents re-renders when unrelated parent state changes
const MenuItem = memo(MenuItemComponent);

export default MenuItem;
